// src/oura/merchant/SetupFlow.tsx
//
// The merchant's Setup page as one guided flow (one setup): the questions
// about their business and the three accesses we need, on one page, one step
// at a time. A rail on the left says how far they are and lets them jump; the
// step on the right says why we ask, how to do it on their own platform, and
// has the one button that moves it on. Finishing a step moves to the next one
// still theirs, and the last view says what happens now.
//
// Saved as they go: every press is saved on the server, so they can stop and
// come back from the same link. Any step can be sent to the person who looks
// after it.
//
// Replaces the Setup list (Access.tsx) while ONE_SETUP_UI is on. Same routes,
// same records: nothing here needs the backend to change, and the questions
// link to the Google Form until the backend takes them on the page.

import { useEffect, useRef, useState, type ReactNode } from "react";
import { ArrowRightIcon, BarChart3Icon, CheckIcon, ChevronRightIcon, GlobeIcon, InfoIcon, ListChecksIcon, Loader2Icon, MapPinIcon, ShieldCheckIcon, StoreIcon } from "lucide-react";
import { cn } from "../lib/cn";
import { BentoStack, FOCUS_RING, FoldCard, PageHead, PanelIcon, PillButton, PillLink, SURFACE, Surface, TYPE, type PanelIconSpec, ROW_HOVER } from "@oura/ui";
import { NextCardFrame } from "../overview/NextCardFrame";
import { Tooltip } from "@component/tooltip";
import { useViewAs } from "@hook/use-view-as";
import type { AccessItem, MerchantFirstCheck, MerchantOnboarding } from "@service/aeo/onboarding";
import { followMySetup, type FollowedSetup } from "../hooks/useMySetup";
import { Link } from "react-router-dom";
import { callName, dayMonth } from "@oura/onboarding/derive";
import { isTheirsDone, merchantSteps, nextStep, sentAnswers, setupTally, type MerchantStep, type MerchantStepKey, type MerchantStepState } from "@oura/onboarding/oneSetup";
import { ACCESS_WHY } from "@oura/onboarding/guides";
import { ouraHref } from "@oura/nav";
import { AccessDrawer } from "./AccessDrawer";
import { GbpStep, GoogleStep, WebsiteStep } from "./setup/AccessSteps";
import { FormAnswered, FormByLink } from "./setup/FormStep";
import { Questions } from "./setup/Questions";
import { SendOn } from "./setup/SendOn";
import { REPORTS_PAGE } from "../merchantV2";

type View = MerchantStepKey | "end";

/** Why each step is asked for, under its title. The questions' own; the accesses' from the guides. */
const WHY: Record<MerchantStepKey, string> = {
    form: "What you sell, where, and who buys it. Everything we write for you starts here.",
    gbp: ACCESS_WHY.gbp,
    website: ACCESS_WHY.website,
    google: "So we can see what people search before they find you, and how many visit.",
};

export function SetupFlow({ embedded = false }: { embedded?: boolean }) {
    /** Undefined while loading; null when this business has no set-up. */
    const [data, setData] = useState<MerchantOnboarding | null | undefined>(undefined);
    const [failed, setFailed] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const [view, setView] = useState<View | null>(null);
    const [managing, setManaging] = useState<AccessItem | null>(null);
    const pane = useRef<HTMLElement>(null);
    const timer = useRef<number | null>(null);
    const { active: readOnly } = useViewAs();

    const followed = useRef<FollowedSetup | null>(null);

    // Read again while the first AI check moves on by itself, so the page follows it.
    useEffect(() => {
        let loaded = false;
        const f = followMySetup(
            (m) => {
                loaded = true;
                setData(m);
                setFailed(false);
            },
            // A later read that fails keeps the page as it was.
            () => {
                if (!loaded) setFailed(true);
            },
        );
        followed.current = f;
        return () => {
            f.stop();
            followed.current = null;
        };
    }, [attempt]);

    useEffect(() => () => {
        if (timer.current) window.clearTimeout(timer.current);
    }, []);

    const steps = data ? merchantSteps(data) : [];
    // Opens on the first step still theirs, else on what happens next.
    const shown: View = view ?? nextStep(steps) ?? "end";

    const go = (next: View, focus = true) => {
        // A press beats a pending move on: the merchant chose where to go.
        if (timer.current) window.clearTimeout(timer.current);
        timer.current = null;
        setView(next);
        if (!focus) return;
        // On a phone the step is under the rail: bring it up, and put focus on its title.
        window.requestAnimationFrame(() => {
            const head = pane.current?.querySelector<HTMLElement>("[data-step-title]");
            head?.focus({ preventScroll: true });
            if (window.matchMedia?.("(max-width: 1023px)").matches) pane.current?.scrollIntoView({ block: "start", behavior: "smooth" });
        });
    };

    const changed = (updated: MerchantOnboarding, advance: boolean) => {
        const from = shown === "end" ? null : shown;
        setData(updated);
        followed.current?.saw(updated);
        if (!advance) return;
        // Straight on to the next step: the tick on "Your setup" says this one
        // is done, so no in-between screen flashes up first (Jack).
        go(nextStep(merchantSteps(updated), from) ?? "end");
    };

    const head = !embedded && <PageHead title="Setup" sentence={<Sentence />} />;

    if (failed) {
        return (
            <>
                {head}
                <Surface>
                    <h2 className={cn(TYPE.tt, "m-0 text-gray-900")}>Couldn't load your setup</h2>
                    <p className={cn(TYPE.tb, "m-0 mt-1 text-gray-500")}>Nothing is lost. Try again.</p>
                    <PillButton
                        className="mt-4 self-start max-sm:h-9"
                        onClick={() => {
                            setFailed(false);
                            setData(undefined);
                            setAttempt((n) => n + 1);
                        }}
                    >
                        Try again
                    </PillButton>
                </Surface>
            </>
        );
    }
    if (data === undefined) {
        return (
            <div role="status" aria-label="Loading your setup" className="flex justify-center py-24">
                <Loader2Icon aria-hidden className="h-6 w-6 animate-spin text-gray-400" />
            </div>
        );
    }
    if (!data || steps.length === 0) {
        return (
            <>
                {head}
                <Surface>
                    <h2 className={cn(TYPE.tt, "m-0 text-gray-900")}>Nothing to set up yet</h2>
                    <p className={cn(TYPE.tb, "m-0 mt-1 text-gray-500")}>Your account manager will start this with you.</p>
                </Surface>
            </>
        );
    }

    const tally = setupTally(steps);
    const current = shown === "end" ? null : steps.find((s) => s.key === shown) ?? null;
    const index = current ? steps.indexOf(current) : -1;

    return (
        <>
            {head}
            <div className="space-y-5">
                <NextCardFrame
                    complete={!current && data.firstCheck?.status === "ready"}
                    tag={current ? { label: current.state === "open" ? current.meta : STATE_WORD[current.state] || current.meta, tone: "blue" } : tally.done === tally.total && data.firstCheck?.status !== "ready" ? { label: "With us", tone: "violet" } : null}
                    title={current ? NEXT_TITLE[current.key] : tally.done === tally.total ? endTitle(data) : "A few steps are still open"}
                    why={current ? whyFor(current.key, data) : tally.done === tally.total ? endWhy(data) : "Everything you have done is saved."}
                    percent={null}
                    progressLabel=""
                    inlineTag
                />

                {/* Wide left for the step itself, a side column for where they are
                    and what to know. Split only from 1280px, so the questions are
                    never squeezed beside a list. */}
                <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] [&>*]:min-w-0">
                    <section ref={pane} aria-live="polite" className={cn(SURFACE.card, "min-w-0 scroll-mt-4 p-5 sm:p-6")}>
                        {current ? (
                            <div key={current.key} className="space-y-6">
                                <header className="flex items-center gap-3">
                                    <PanelIcon icon={STEP_ICON[current.key].icon} area={STEP_ICON[current.key].area} />
                                    <h2 data-step-title tabIndex={-1} className={cn(TYPE.tt, "m-0 min-w-0 flex-1 text-gray-900 outline-none")}>
                                        {current.title}
                                    </h2>
                                    <span className={cn(TYPE.tc, "shrink-0 tabular-nums text-gray-500")}>
                                        Step {index + 1} of {steps.length}
                                    </span>
                                </header>
                                <StepBody step={current} data={data} readOnly={readOnly} onChanged={changed} onManage={setManaging} />
                                <StepFoot
                                    steps={steps}
                                    current={current}
                                    onGo={go}
                                    sendOn={
                                        current.state !== "done" && current.state !== "waiting" ? (
                                            <SendOn step={current} onboarding={data} readOnly={readOnly} onChanged={(u) => changed(u, false)} />
                                        ) : null
                                    }
                                />
                            </div>
                        ) : (
                            <AllSet steps={steps} data={data} onGo={go} />
                        )}
                    </section>

                    <BentoStack>
                        <FoldCard id="setup-steps" title="Your setup" icon={{ icon: ListChecksIcon, area: "work" }} foldedFact={`${tally.done} of ${tally.total}`} alwaysOpen>
                            <SetupList steps={steps} shown={shown} done={tally.done} total={tally.total} minutesLeft={tally.minutesLeft} check={data.firstCheck ?? null} onGo={go} />
                        </FoldCard>
                        <FoldCard id="setup-know" title="Good to know" icon={{ icon: ShieldCheckIcon, area: "setup" }} foldedFact="No passwords">
                            <dl className="m-0 divide-y divide-gray-100">
                                <Fact label="Passwords">We never ask for one. You add us, and you can remove us any time.</Fact>
                                <Fact label="Saving">Automatic. Stop any time and come back from the same link.</Fact>
                                <Fact label="Not yours to do?">Each access step has "Someone else looks after this?" to email it to them.</Fact>
                                <Fact label="Questions">
                                    {callName(data.accountManager.name) || "Your account manager"}
                                    {data.accountManager.email && <span className="text-gray-500"> · {data.accountManager.email}</span>}
                                </Fact>
                            </dl>
                        </FoldCard>
                    </BentoStack>
                </div>
            </div>

            {managing && (
                <AccessDrawer
                    key={managing.key}
                    item={data.access.find((a) => a.key === managing.key) ?? managing}
                    onboarding={data}
                    nextKey={null}
                    onClose={() => setManaging(null)}
                    onChanged={(m) => {
                        setData(m);
                        followed.current?.saw(m);
                    }}
                    onNext={() => setManaging(null)}
                />
            )}
        </>
    );
}

/** The page sentence, with the password promise behind an info icon beside it. */
function Sentence() {
    return (
        <span className="inline-flex items-center gap-1.5">
            A few things from you, then we take it from here.
            <Tooltip side="right" asChild content="We never ask for your password. You add us, and you can remove us any time.">
                <button type="button" aria-label="We never ask for your password" className={cn("rounded-full text-gray-400 hover:text-gray-600", FOCUS_RING)}>
                    <InfoIcon aria-hidden size={14} />
                </button>
            </Tooltip>
        </span>
    );
}

/** Short names, for the Next button under a step. */
const SHORT: Record<MerchantStepKey, string> = { form: "Your business", gbp: "Google profile", website: "Website", google: "Google data" };

/** What the Next card asks for, in the merchant's words. */
const NEXT_TITLE: Record<MerchantStepKey, string> = {
    form: "Tell us about your business",
    gbp: "Add us to your Google profile",
    website: "Add us to your website",
    google: "Connect Search Console and Analytics",
};

const STEP_ICON: Record<MerchantStepKey, PanelIconSpec> = {
    form: { icon: StoreIcon, area: "google" },
    gbp: { icon: MapPinIcon, area: "google" },
    website: { icon: GlobeIcon, area: "site" },
    google: { icon: BarChart3Icon, area: "google" },
};

const STATE_WORD: Record<MerchantStepState, string> = { done: "Done", waiting: "We're confirming", with: "", open: "" };

/** One label and its value on a line, as the staff Client card draws them. */
function Fact({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="flex flex-col gap-0.5 py-2.5 first:pt-0 last:pb-0 sm:flex-row sm:gap-4">
            <dt className={cn(TYPE.tb, "m-0 shrink-0 text-gray-500 sm:w-32")}>{label}</dt>
            <dd className={cn(TYPE.tb, "m-0 min-w-0 flex-1 text-gray-900 [overflow-wrap:anywhere]")}>{children}</dd>
        </div>
    );
}

/** The mark before each step, as the staff Steps card draws it: colour and shape both carry the state. */
function Mark({ state, on }: { state: MerchantStepState; on: boolean }) {
    if (state === "done" || state === "waiting") {
        return (
            <span className={cn("grid h-5 w-5 place-items-center rounded-full", state === "done" ? "bg-emerald-500 text-white" : "bg-emerald-100 text-emerald-700")}>
                <CheckIcon size={12} strokeWidth={3} />
            </span>
        );
    }
    // With someone else: waiting, so grey. The step open now is blue.
    const ring = on ? "border-blue-600" : state === "with" ? "border-gray-400" : "border-gray-300";
    const dot = on ? "bg-blue-600" : state === "with" ? "bg-gray-400" : "bg-transparent";
    return (
        <span className={cn("grid h-5 w-5 place-items-center rounded-full border-2 bg-white", ring)}>
            <span className={cn("h-2 w-2 rounded-full", dot)} />
        </span>
    );
}

/**
 * Where they are: the four steps as rows, each with where it stands on the
 * right. Every row opens its step; the one open now is blue.
 */
function SetupList({
    steps,
    shown,
    done,
    total,
    minutesLeft,
    check,
    onGo,
}: {
    steps: MerchantStep[];
    shown: View;
    done: number;
    total: number;
    minutesLeft: number;
    check: MerchantFirstCheck | null;
    onGo: (v: View) => void;
}) {
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
    const left = done === total ? "Your part is done." : minutesLeft > 0 ? `About ${minutesLeft} min left.` : "The rest is with someone else.";
    return (
        <nav aria-label="Setup steps">
            <div className="mb-2 flex items-baseline justify-between gap-3">
                <span className={cn(TYPE.tc, "tabular-nums text-gray-500")}>{left}</span>
                <span className={cn(TYPE.tm, "tabular-nums", done === total ? "font-medium text-emerald-600" : "text-gray-400")}>
                    {done} of {total}
                </span>
            </div>
            <div role="progressbar" aria-label="Your part of the setup" aria-valuemin={0} aria-valuemax={total} aria-valuenow={done} className="mb-2 h-1.5 overflow-hidden rounded-full bg-gray-100">
                <div className="h-full rounded-full bg-blue-600 transition-[width] duration-500 motion-reduce:transition-none" style={{ width: `${pct}%` }} />
            </div>
            <ol className="list-plain m-0 divide-y divide-gray-100 p-0">
                {steps.map((s) => {
                    const on = shown === s.key;
                    return (
                        <li key={s.key}>
                            <button
                                type="button"
                                onClick={() => onGo(s.key)}
                                aria-current={on ? "step" : undefined}
                                className={cn("flex min-h-11 w-full items-center gap-3 rounded-md text-left", ROW_HOVER, FOCUS_RING)}
                            >
                                <span aria-hidden className="flex w-5 shrink-0 justify-center">
                                    <Mark state={s.state} on={on} />
                                </span>
                                <span className={cn(TYPE.tb, "min-w-0 flex-1 sm:truncate", on ? "font-medium text-blue-600" : "text-gray-900 group-hover:text-blue-600")}>{s.title}</span>
                                <span className={cn(TYPE.tc, "shrink-0 tabular-nums", "text-gray-500")}>
                                    {s.state === "waiting" ? "We're confirming" : s.meta}
                                </span>
                                <ChevronRightIcon aria-hidden size={14} className="shrink-0 text-gray-300 group-hover:text-gray-500" />
                            </button>
                            {/* The first AI check hangs off step 1, the step that starts it, so
                                "after step 1" reads where it sits (Jack). Ours, so indented. */}
                            {check && s.key === "form" && (
                                <div className="relative pb-1 pl-8">
                                    <span aria-hidden className="absolute left-[9px] top-0 h-4 w-3 rounded-bl-md border-b-2 border-l-2 border-gray-200" />
                                    <CheckRow check={check} />
                                </div>
                            )}
                        </li>
                    );
                })}
                {check && !steps.some((s) => s.key === "form") && (
                    <li>
                        <CheckRow check={check} />
                    </li>
                )}
            </ol>
        </nav>
    );
}

/** The Next card once their part is done: where the first AI check is, in one line. */
function endTitle(data: MerchantOnboarding): string {
    switch (data.firstCheck?.status) {
        case "running":
        case "reviewing": return "Your first AI check is in progress";
        case "ready": return "Your results and plan are ready";
        default: return "We're preparing your first AI check";
    }
}

function endWhy(data: MerchantOnboarding): string {
    switch (data.firstCheck?.status) {
        case "running":
        case "reviewing": return "We run it, then our team checks the results. Your Baseline Snapshot and month-by-month plan arrive together by email.";
        case "ready": return `Your Baseline Snapshot and month-by-month plan are in ${REPORTS_PAGE}.`;
        default: return "Our team is checking the 50 questions from your answers, then starts it. Nothing for you to do.";
    }
}

/** Under the Next card's title: why we ask, or, once the answers are in, that the first AI check is on its way. */
function whyFor(key: MerchantStepKey, data: MerchantOnboarding): string {
    const check = data.firstCheck?.status;
    if (key === "google" && (check === "preparing" || check === "running" || check === "reviewing")) {
        return "Thanks for your answers. Our team is setting up your first AI check from them, so keep going with access meanwhile.";
    }
    return WHY[key];
}

/** What the First AI check row says, by where it stands. Never a date or a countdown we cannot keep. */
const CHECK_FACT: Record<MerchantFirstCheck["status"], string> = {
    // It can run before the answers are in (Jack, 21 Oct 2026): staff start it.
    after_answers: "Our team starts it",
    preparing: "Preparing",
    // One word for the merchant while it is with us: they need to know it is moving, not which half.
    running: "In progress",
    // Finished and with our team: done, on the day it finished, as staff's Steps say it (#4443).
    reviewing: "Done",
    ready: "Ready",
};

/**
 * The fifth row: ours, not theirs, so it is not counted in their steps and is
 * never a button to a step. It moves on its own from the check's status;
 * finished, it opens what AI says about them.
 */
function CheckRow({ check }: { check: MerchantFirstCheck }) {
    const ready = check.status === "ready";
    // Finished, results with our team: marked done, but nothing to open until we send them.
    const finished = ready || check.status === "reviewing";
    const fact = check.status === "reviewing" ? dayMonth(check.finishedAt) || CHECK_FACT.reviewing : CHECK_FACT[check.status];
    const body = (
        <>
            <span aria-hidden className="flex w-5 shrink-0 justify-center">
                {finished ? (
                    <span className="grid h-5 w-5 place-items-center rounded-full bg-emerald-500 text-white">
                        <CheckIcon size={12} strokeWidth={3} />
                    </span>
                ) : (
                    <span className={cn("grid h-5 w-5 place-items-center rounded-full border-2 bg-white", check.status === "after_answers" ? "border-gray-300" : "border-blue-600")}>
                        <span className={cn("h-2 w-2 rounded-full", check.status === "after_answers" ? "bg-transparent" : "bg-blue-600", check.status === "running" && "motion-safe:animate-pulse")} />
                    </span>
                )}
            </span>
            <span className="min-w-0 flex-1">
                <span className={cn(TYPE.tb, "block", ready ? "text-gray-900 group-hover:text-blue-600" : "text-gray-900")}>First AI check</span>
                <span className={cn(TYPE.tm, "block text-gray-500")}>By Oura</span>
            </span>
            <span className={cn(TYPE.tc, "min-w-0 max-w-[50%] text-right tabular-nums", ready ? "font-medium text-blue-600" : "text-gray-500")}>{ready ? "See results" : fact}</span>
            <span aria-hidden className="w-3.5 shrink-0">{ready && <ChevronRightIcon size={14} className="text-gray-300 group-hover:text-gray-500" />}</span>
        </>
    );
    const shell = cn("group flex min-h-11 w-full items-center gap-3 rounded-md py-1.5 text-left", FOCUS_RING);
    return ready ? (
        <Link to={ouraHref("questions", { role: "oura-client" })} className={shell}>
            {body}
        </Link>
    ) : (
        <div className={shell}>{body}</div>
    );
}

function StepBody({
    step,
    data,
    readOnly,
    onChanged,
    onManage,
}: {
    step: MerchantStep;
    data: MerchantOnboarding;
    readOnly: boolean;
    onChanged: (updated: MerchantOnboarding, advance: boolean) => void;
    onManage: (item: AccessItem) => void;
}) {
    const props = { step, onboarding: data, readOnly, onChanged, onManage };
    switch (step.key) {
        case "form":
            if (data.form?.answeredAt) return <FormAnswered at={data.form.answeredAt} answers={sentAnswers(data.form)} />;
            // Staff viewing as the merchant may answer these for them when the backend says so (#4497); every other step stays read-only.
            return data.form?.inPlatform
                ? <Questions onboarding={data} readOnly={readOnly && data.form.staffMayAnswer !== true} onBehalf={readOnly && data.form.staffMayAnswer === true} onChanged={onChanged} />
                : <FormByLink onboarding={data} />;
        // Staff viewing as the merchant may confirm these two for them, silently, when the backend says so (#4510).
        case "gbp":
            return <GbpStep {...props} readOnly={readOnly && data.staffMayClaimAccess !== true} onBehalf={readOnly && data.staffMayClaimAccess === true} />;
        case "website":
            return <WebsiteStep {...props} readOnly={readOnly && data.staffMayClaimAccess !== true} onBehalf={readOnly && data.staffMayClaimAccess === true} />;
        // Connect stays read-only in view-as; "I've added you" (#4513) follows the same staff rule as the website and Google profile.
        case "google":
            return <GoogleStep {...props} onBehalf={readOnly && data.staffMayClaimAccess === true} />;
    }
}

/**
 * Under each step, one row: hand it to someone else on the left, move on on
 * the right. Once their part is done the move on is the blue button; before
 * that it is a quiet skip, so the step's own button stays the one to press.
 */
function StepFoot({ steps, current, onGo, sendOn }: { steps: MerchantStep[]; current: MerchantStep; onGo: (v: View) => void; sendOn: ReactNode }) {
    const next = nextStep(steps, current.key);
    const target: View = next && next !== current.key ? next : "end";
    const finished = isTheirsDone(current);
    const label = target === "end" ? "Finish" : `Next: ${SHORT[target]}`;
    // Step 1 starts the first AI check: putting it off says what it holds up.
    const later = current.key === "form" && !finished;
    return (
        <div className="flex flex-wrap items-start justify-between gap-3 border-t border-gray-100 pt-4">
            <div className="min-w-0 flex-1 basis-60">{sendOn}</div>
            <div className="flex flex-col items-end gap-1">
                <PillButton variant={finished ? "primary" : "text"} className={finished ? "max-sm:h-9" : "text-gray-500"} onClick={() => onGo(target)}>
                    {finished ? label : later ? "Do this later" : "Skip for now"}
                    <ArrowRightIcon aria-hidden size={14} />
                </PillButton>
                {later && <p className={cn(TYPE.tm, "m-0 max-w-[260px] text-right text-gray-500")}>Your first AI check starts once these answers are in.</p>}
            </div>
        </div>
    );
}

function AllSet({ steps, data, onGo }: { steps: MerchantStep[]; data: MerchantOnboarding; onGo: (v: View) => void }) {
    const left = steps.filter((s) => !isTheirsDone(s));
    const waiting = steps.some((s) => s.state === "waiting");
    if (left.length > 0) {
        return (
            <div className="space-y-5">
                <header>
                    <h2 data-step-title tabIndex={-1} className={cn(TYPE.num, "m-0 text-gray-900 outline-none")}>
                        Almost there
                    </h2>
                    <p className={cn(TYPE.tb, "m-0 mt-1.5 text-gray-600")}>
                        {left.length === 1 ? "One step left." : `${left.length} steps left.`} Everything you have done is saved.
                    </p>
                </header>
                <ul className="list-plain m-0 divide-y divide-gray-100 p-0">
                    {left.map((s) => (
                        <li key={s.key} className="flex flex-wrap items-center gap-3 py-3">
                            <span className="min-w-0 flex-1">
                                <span className={cn(TYPE.tb, "block font-medium text-gray-900")}>{s.title}</span>
                                <span className={cn(TYPE.tc, "block text-gray-500")}>{s.meta}</span>
                            </span>
                            <PillButton variant={s.state === "open" ? "primary" : "secondary"} className="max-sm:h-9" onClick={() => onGo(s.key)}>
                                {s.state === "open" ? "Do it now" : "Open"}
                            </PillButton>
                        </li>
                    ))}
                </ul>
            </div>
        );
    }
    // Their part is done, so the page turns to what comes next: the same
    // First AI check the side card shows, in the same words, one line each.
    const check = data.firstCheck ?? null;
    const ready = check?.status === "ready";
    const running = check?.status === "running" || check?.status === "reviewing";
    return (
        <div className="space-y-6">
            <header className="flex items-start gap-4">
                <span aria-hidden className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-emerald-500 text-white">
                    <CheckIcon size={24} strokeWidth={2.5} />
                </span>
                <div className="min-w-0">
                    <h2 data-step-title tabIndex={-1} className={cn(TYPE.num, "m-0 text-gray-900 outline-none")}>
                        You're all set
                    </h2>
                    <p className={cn(TYPE.tb, "m-0 mt-1 max-w-xl text-gray-600")}>
                        {ready
                            ? "Your part is done, and your results and plan are ready."
                            : running
                              ? "Your part is done. Your first AI check is in progress."
                                : "Your part is done. Next, we run your first AI check."}
                    </p>
                </div>
            </header>
            <ol className="list-plain m-0 p-0">
                <Stage state="done" title="Your setup" last={false}>
                    {waiting ? "Done. We confirm the invites you sent, nothing for you to do." : "Done."}
                </Stage>
                {check && (
                    <Stage state={ready ? "done" : "now"} title="First AI check" fact={CHECK_FACT[check.status]} last={false}>
                        {ready
                            ? "Finished. Everything we do is measured from it."
                            : running
                              ? "We ask ChatGPT, Gemini and Google's AI 50 questions your customers ask, then our team checks the results."
                              : "Our team is preparing your 50 questions from your answers, then starts it. Nothing for you to do."}
                    </Stage>
                )}
                <Stage state={ready ? "done" : "later"} title="Your results and plan" last>
                    {ready
                        ? `Your Baseline Snapshot and month-by-month plan are in ${REPORTS_PAGE}.`
                        : "Your Baseline Snapshot and month-by-month plan, sent together by email."}
                </Stage>
            </ol>
            {ready ? (
                <div className="flex flex-wrap gap-2">
                    <PillLink variant="primary" to={ouraHref("questions", { role: "oura-client" })} className="max-sm:h-9">
                        See what AI says about you
                    </PillLink>
                    <PillLink to={ouraHref("reports", { role: "oura-client" })} className="max-sm:h-9">
                        Open my snapshot and plan
                    </PillLink>
                </div>
            ) : (
                <PillLink variant="primary" to={ouraHref("dashboard", { role: "oura-client" })} className="max-sm:h-9">
                    Go to Home
                </PillLink>
            )}
        </div>
    );
}

/** One stage on the end screen's line: the side card's marks, joined top to bottom. */
function Stage({ state, title, fact, last, children }: { state: "done" | "now" | "later"; title: string; fact?: string; last: boolean; children: ReactNode }) {
    return (
        <li className="relative flex gap-3 pb-5 last:pb-0">
            {!last && <span aria-hidden className={cn("absolute left-[9px] top-6 h-[calc(100%-20px)] w-0.5 rounded-full", state === "done" ? "bg-emerald-200" : "bg-gray-200")} />}
            <span aria-hidden className="relative mt-0.5 flex w-5 shrink-0 justify-center">
                {state === "done" ? (
                    <span className="grid h-5 w-5 place-items-center rounded-full bg-emerald-500 text-white">
                        <CheckIcon size={12} strokeWidth={3} />
                    </span>
                ) : (
                    <span className={cn("grid h-5 w-5 place-items-center rounded-full border-2 bg-white", state === "now" ? "border-blue-600" : "border-gray-300")}>
                        <span className={cn("h-2 w-2 rounded-full", state === "now" ? "bg-blue-600" : "bg-transparent")} />
                    </span>
                )}
            </span>
            <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-baseline justify-between gap-x-3">
                    <span className={cn(TYPE.tb, "font-medium", state === "later" ? "text-gray-500" : "text-gray-900")}>{title}</span>
                    {fact && <span className={cn(TYPE.tc, "text-gray-500")}>{fact}</span>}
                </span>
                <span className={cn(TYPE.tc, "mt-0.5 block text-gray-600")}>{children}</span>
            </span>
        </li>
    );
}

